import React, { useEffect, useState } from "react";

const PatientReviewsSection = ({ businessName, placeId }) => {
  const [selectedReview, setSelectedReview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [screenSize, setScreenSize] = useState("desktop");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [overallRating, setOverallRating] = useState(4.5);
  const [totalReviews, setTotalReviews] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Check scroll position
  const checkScrollPosition = () => {
    const container = document.getElementById("reviews-container");
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    const container = document.getElementById("reviews-container");
    if (container) {
      container.addEventListener("scroll", checkScrollPosition);
      checkScrollPosition(); // Initial check
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScrollPosition);
      }
    };
  }, [reviews]); // Reattach when reviews change

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setScreenSize("mobile");
      } else if (width < 1024) {
        setScreenSize("tablet");
      } else {
        setScreenSize("desktop");
      }
    };

    handleResize(); // Set initial value
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        let url;
        if (placeId) {
          url = `${
            process.env.REACT_APP_GOOGLE_REVIEW_URL
          }/reviews?placeId=${encodeURIComponent(placeId)}`;
        } else {
          url = `${
            process.env.REACT_APP_GOOGLE_REVIEW_URL
          }/reviews?places=${encodeURIComponent(businessName)}`;
        }
        const response = await fetch(url);

        const htmlText = await response.text();
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(htmlText, "text/html");

        // Initialize with default values
        let overallRating = 4.5;
        let totalReviews = 0;

        // Find review count - look for exact match of "Reviews" text
        const reviewCountElements = Array.from(
          htmlDoc.querySelectorAll("p, span, div")
        );
        const reviewCountElement = reviewCountElements.find((el) => {
          const text = el.textContent.trim();
          return text.includes("Reviews") || text.includes("reviews");
        });

        if (reviewCountElement) {
          const reviewsText = reviewCountElement.textContent.trim();
          const reviewsMatch = reviewsText.match(/([\d,]+)\s*Reviews?/i);
          if (reviewsMatch) {
            totalReviews = parseInt(reviewsMatch[1].replace(/,/g, ""));
          }
        }

        // Find rating - look for text containing "RATING"
        const ratingElements = Array.from(htmlDoc.querySelectorAll("p"));
        const ratingElement = ratingElements.find((el) =>
          el.textContent.trim().includes("RATING")
        );

        if (ratingElement) {
          const ratingMatch = ratingElement.textContent.match(/(\d+\.\d+)/);
          if (ratingMatch) overallRating = parseFloat(ratingMatch[1]);
        }

        // Extract individual reviews
        const reviewCards = htmlDoc.querySelectorAll(
          '[class*="w-[300px]"], [class*="w-[386px]"], [class*="w-[500px]"]'
        );

        const extractedReviews = Array.from(reviewCards).map((card, index) => {
          // Get profile image - use direct src attribute
          const imgElement = card.querySelector("img.rounded-full");
          const avatar =
            imgElement?.src ||
            "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face";

          // Fix Google profile image URL by removing size parameters if they exist
          let finalAvatar = avatar;
          if (
            avatar.includes("googleusercontent.com") &&
            avatar.includes("=s")
          ) {
            finalAvatar = avatar.split("=s")[0] + "=s128"; // Standardize to 128px
          }

          const name =
            card.querySelector("h5")?.textContent.trim() || `User ${index + 1}`;
          const stars = parseFloat(
            card
              .querySelector('[aria-label*="star"]')
              ?.getAttribute("aria-label")
              ?.match(/\d+\.?\d*/)?.[0] ||
              card.querySelectorAll('[class*="text-yellow-300"]').length ||
              5
          );
          // const stars =
          //   card.querySelectorAll('[class*="text-yellow-300"]').length || 5;
          const date =
            card.querySelector('[class*="bg-green-100"]')?.textContent.trim() ||
            "Recent";
          const reviewText =
            card.querySelector("p:not(.text-gray-500)")?.textContent.trim() ||
            "";

          return {
            id: index + 1,
            name,
            avatar: finalAvatar,
            rating: stars,
            date,
            review:
              reviewText.length > 100
                ? `${reviewText.substring(0, 100)}...`
                : reviewText,
            fullReview: reviewText,
          };
        });

        setReviews(extractedReviews);
        setOverallRating(overallRating);
        setTotalReviews(totalReviews || extractedReviews.length);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching reviews:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    const shouldFetch = businessName || placeId;
    if (shouldFetch) {
      fetchReviews();
    }
  }, [businessName, placeId]);
  // const renderStars = (rating) => {
  //   return Array.from({ length: 5 }, (_, index) => (
  //     <i
  //       key={index}
  //       className={`fas fa-star text-${
  //         index < rating ? "warning" : "muted"
  //       } me-1`}
  //       style={{ fontSize: "14px" }}
  //     ></i>
  //   ));
  // };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <>
        {[...Array(fullStars)].map((_, i) => (
          <i
            key={`full-${i}`}
            className="fas fa-star text-warning me-1"
            style={{ fontSize: "14px" }}
          ></i>
        ))}

        {hasHalfStar && (
          <i
            key="half"
            className="fas fa-star-half-alt text-warning me-1"
            style={{ fontSize: "14px" }}
          ></i>
        )}

        {[...Array(emptyStars)].map((_, i) => (
          <i
            key={`empty-${i}`}
            className="far fa-star text-muted me-1"
            style={{ fontSize: "14px" }}
          ></i>
        ))}
      </>
    );
  };
  const scrollLeft = () => {
    const container = document.getElementById("reviews-container");
    container.scrollBy({ left: -container.offsetWidth, behavior: "smooth" });
    setTimeout(checkScrollPosition, 400); // Check after scroll animation
  };

  const scrollRight = () => {
    const container = document.getElementById("reviews-container");
    container.scrollBy({ left: container.offsetWidth, behavior: "smooth" });
    setTimeout(checkScrollPosition, 400); // Check after scroll animation
  };

  const handleShowFullReview = (review) => {
    if (review.review) {
      setSelectedReview(review);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedReview(null);
  };

  if (loading) {
    return (
      <div className="container py-5" style={{ backgroundColor: "#f8f9fa" }}>
        <div className="container text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading reviews...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="container-fluid py-5"
        style={{ backgroundColor: "#f8f9fa" }}
      >
        <div className="container text-center py-5">
          <div className="alert alert-danger">
            Error loading reviews: {error}
          </div>
        </div>
      </div>
    );
  }

  const ProfileAvatar = ({ src, name, size = 50 }) => {
    const [imgError, setImgError] = useState(false);

    // Extract first letter of the first name
    const getInitial = () => {
      if (!name) return "?";
      const names = name.split(" ");
      return names[0].charAt(0).toUpperCase();
    };

    // Generate a consistent background color based on the name
    const getBackgroundColor = () => {
      if (!name) return "#cccccc";
      const colors = [
        "#FFB6C1",
        "#FFD700",
        "#98FB98",
        "#87CEFA",
        "#FFA07A",
        "#9370DB",
        "#20B2AA",
        "#FF6347",
      ];
      const charCode = name.charCodeAt(0) || 0;
      return colors[charCode % colors.length];
    };

    return (
      <div
        className="rounded-circle d-flex align-items-center justify-content-center"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundColor: imgError ? getBackgroundColor() : "transparent",
          fontSize: `${size * 0.4}px`,
          fontWeight: "bold",
          color: "white",
          overflow: "hidden",
          position: "relative",
          flexShrink: 0,
        }}
      >
        {!imgError ? (
          <img
            src={src}
            alt={name}
            className="rounded-circle"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              position: "absolute",
              top: 0,
              left: 0,
            }}
            onError={() => setImgError(true)}
            loading="lazy"
          />
        ) : (
          <span>{getInitial()}</span>
        )}
      </div>
    );
  };

  return (
    <>
      {reviews.length > 0 && (
        <div
          className="container-fluid py-5"
          style={{ backgroundColor: "#f8f9fa" }}
        >
          <link
            rel="stylesheet"
            href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css"
          />

          <div className="container">
            {/* Header */}
            <div className="text-center mb-5">
              <h2 className="fw-bold text-dark">What Patients Are Saying</h2>
            </div>

            {/* Reviews Grid with Scroll */}
            <>
              <div className="position-relative">
                <div
                  id="reviews-container"
                  className="d-flex overflow-auto"
                  style={{
                    width: "100%",
                    scrollSnapType: "x mandatory",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    gap: "20px",
                    justifyContent: reviews.length <= 2 && "center",
                  }}
                >
                  <style jsx>{`
                    #reviews-container::-webkit-scrollbar {
                      display: none;
                    }
                  `}</style>

                  {reviews.map((review) => (
                    <div
                      key={review.id}
                      className="review-card flex-shrink-0"
                      style={{
                        width:
                          screenSize === "mobile"
                            ? "100%"
                            : screenSize === "tablet"
                            ? "calc(50% - 10px)"
                            : reviews.length <= 2
                            ? "400px"
                            : "calc(33.33% - 14px)",
                        scrollSnapAlign: "start",
                      }}
                    >
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body p-4">
                          {/* User Info */}
                          <div className="d-flex align-items-center mb-3">
                            <ProfileAvatar
                              src={review.avatar}
                              name={review.name}
                              size={50}
                            />
                            <div style={{ paddingLeft: "10px" }}>
                              <h6 className="mb-1 fw-semibold">
                                {review.name}
                              </h6>
                              <div className="mb-2">
                                {renderStars(review.rating)}
                              </div>
                            </div>
                          </div>

                          {/* Review Text */}
                          <p
                            className="text-muted mb-3"
                            style={{ fontSize: "0.95rem", lineHeight: "1.5" }}
                          >
                            {review.review}
                          </p>

                          {/* Date and See All Link */}
                          <div className="d-flex justify-content-between align-items-center">
                            <small
                              className="text-success d-flex align-items-center"
                              style={{
                                backgroundColor: "rgb(112 210 164 / 19%)",
                                padding: "7px 8px",
                                borderRadius: "6px",
                              }}
                            >
                              <i
                                className="fas fa-calendar-alt text-success me-2"
                                style={{ fontSize: "12px" }}
                              ></i>
                              {review.date}
                            </small>
                            {review.fullReview.length > 100 && (
                              <button
                                type="button"
                                className="btn btn-link text-primary text-decoration-none small p-0"
                                onClick={() => handleShowFullReview(review)}
                              >
                                See all
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom Summary */}
              <div className="row mt-5 align-items-center">
                <div className="col-12 col-sm-8 mb-3 mb-sm-0 d-none d-sm-block">
                  <div
                    className="d-flex align-items-center"
                    style={{ flexWrap: "nowrap", whiteSpace: "nowrap" }}
                  >
                    <span className="text-muted me-2">
                      <i className="fab fa-google fa px-1"></i>
                    </span>
                    <div className="me-2" style={{ minWidth: "90px" }}>
                      {renderStars(overallRating)}
                    </div>
                    <span
                      className="fw-bold text-dark me-2"
                      style={{
                        borderRight: "1px solid #ccc",
                        paddingRight: "10px",
                      }}
                    >
                      {overallRating.toFixed(1)} RATING
                    </span>
                    <span className="text-muted">{totalReviews} Reviews</span>
                  </div>
                </div>
                {reviews.length > 3 && (
                  <div className="col-12 col-sm-4">
                    <div className="d-flex justify-content-center justify-content-sm-end align-items-center">
                      <button
                        className={`btn rounded-circle me-2 shadow-sm ${
                          canScrollLeft ? "btn-primary" : "btn-light"
                        }`}
                        style={{ width: "35px", height: "35px" }}
                        onClick={scrollLeft}
                        disabled={!canScrollLeft}
                      >
                        <i
                          className={`fas fa-chevron-left ${
                            canScrollLeft ? "text-white" : "text-muted"
                          }`}
                        ></i>
                      </button>
                      <button
                        className={`btn rounded-circle shadow-sm ${
                          canScrollRight ? "btn-primary" : "btn-light"
                        }`}
                        style={{ width: "35px", height: "35px" }}
                        onClick={scrollRight}
                        disabled={!canScrollRight}
                      >
                        <i
                          className={`fas fa-chevron-right ${
                            canScrollRight ? "text-white" : "text-muted"
                          }`}
                        ></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>

            {/* Modal for Full Review */}
            {showModal && selectedReview && (
              <div
                className="modal fade show d-block"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                tabIndex="-1"
                role="dialog"
              >
                <div
                  className="modal-dialog modal-lg modal-dialog-centered"
                  role="document"
                >
                  <div className="modal-content">
                    <div className="modal-header border-0">
                      <div className="d-flex align-items-center">
                        <ProfileAvatar
                          src={selectedReview.avatar}
                          name={selectedReview.name}
                          size={50}
                        />
                        {/* <img
                      src={selectedReview.avatar}
                      alt={selectedReview.name}
                      className="rounded-circle me-3"
                      width="60"
                      height="60"
                      style={{ objectFit: "cover" }}
                    /> */}
                        <div style={{ paddingLeft: "10px" }}>
                          <h5 className="mb-1 fw-bold">
                            {selectedReview.name}
                          </h5>
                          <div className="mb-0">
                            {renderStars(selectedReview.rating)}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={handleCloseModal}
                        aria-label="Close"
                      ></button>
                    </div>
                    <div className="modal-body pt-0">
                      <div
                        style={{
                          maxHeight: "400px",
                          overflowY: "auto",
                          padding: "10px",
                          backgroundColor: "#cccccc24",
                          borderRadius: "8px",
                        }}
                      >
                        <p
                          className="text-muted mb-3"
                          style={{ lineHeight: "1.6" }}
                        >
                          {selectedReview.fullReview}
                        </p>
                        <small
                          className="text-success d-flex align-items-center"
                          style={{
                            backgroundColor: "rgb(112 210 164 / 19%)",
                            padding: "7px 8px",
                            borderRadius: "6px",
                            width: "fit-content",
                          }}
                        >
                          <i
                            className="fas fa-calendar-alt text-success me-2"
                            style={{ fontSize: "14px" }}
                          ></i>
                          {selectedReview.date}
                        </small>
                      </div>
                    </div>
                    <div className="modal-footer border-0 pt-0"></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PatientReviewsSection;
